import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import useAuthUser from '../services/useAuthUser';
import { getCognidoorApiBase, getCognidoorBuildStamp } from '../config/apiBase';

const STORAGE_KEY = 'ownerOnlyRoute_lastAccessCheck_v1';

function classifyAccessFailure(status, isNetworkError) {
  if (isNetworkError) return 'network_or_cors';
  if (status === 401) return 'http_401';
  if (status === 403) return 'http_403';
  if (status === 404) return 'http_404';
  return 'other';
}

function readStoredFailure() {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeStoredFailure(payload) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* quota / private mode */
  }
}

export default function OwnerOnlyRoute({ children }) {
  const { user, loading } = useAuthUser();
  const [allowed, setAllowed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [denyMeta, setDenyMeta] = useState(null);
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const debugAccess = useMemo(() => {
    const d = searchParams.get('debug');
    const o = searchParams.get('ownerDebug');
    return d === '1' || o === '1';
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setAllowed(false);
      setChecking(false);
      setDenyMeta(null);
      return () => {};
    }
    setChecking(true);
    setDenyMeta(null);
    const apiBase = getCognidoorApiBase();
    const accessUrl = `${apiBase}/api/internal/reviews/access`;
    user
      .getIdToken()
      .then((token) =>
        fetch(accessUrl, {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
          mode: 'cors',
        })
      )
      .then((response) => {
        if (cancelled) return;
        const ok = response.status === 204 || response.ok;
        if (!ok) {
          const reason = classifyAccessFailure(response.status, false);
          console.warn('[OwnerOnlyRoute] /api/internal/reviews/access', response.status, response.statusText, reason);
          const payload = {
            reason,
            status: response.status,
            statusText: response.statusText || '',
            apiBase,
            accessUrl,
            buildStamp: getCognidoorBuildStamp(),
            at: Date.now(),
          };
          writeStoredFailure(payload);
          setDenyMeta(payload);
        } else {
          try {
            sessionStorage.removeItem(STORAGE_KEY);
          } catch {
            /* ignore */
          }
        }
        setAllowed(ok);
        setChecking(false);
      })
      .catch((err) => {
        if (cancelled) return;
        const reason = classifyAccessFailure(null, true);
        console.error('[OwnerOnlyRoute] access check failed', err);
        const payload = {
          reason,
          status: null,
          statusText: err instanceof Error ? err.message : String(err),
          apiBase,
          accessUrl,
          buildStamp: getCognidoorBuildStamp(),
          at: Date.now(),
        };
        writeStoredFailure(payload);
        setDenyMeta(payload);
        setAllowed(false);
        setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (loading || checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (!allowed) {
    const stored = denyMeta || readStoredFailure();
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-lg w-full rounded-2xl border border-slate-200 bg-white p-8 shadow-sm space-y-4">
          <h1 className="text-xl font-semibold text-slate-900">Access restricted</h1>
          <p className="text-slate-600">
            You don&apos;t have access to this internal page. If you believe this is a mistake, contact support with
            the time you tried and your account email.
          </p>
          {debugAccess && stored ? (
            <pre className="text-xs bg-slate-100 text-slate-800 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-all">
              {JSON.stringify(stored, null, 2)}
            </pre>
          ) : null}
          {debugAccess && !stored ? (
            <p className="text-xs text-slate-500">No diagnostic payload (try reloading after a failed check).</p>
          ) : null}
          {!debugAccess ? (
            <p className="text-xs text-slate-500">
              Add <code className="bg-slate-100 px-1 rounded">?debug=1</code> to the URL for technical details (API
              base, HTTP status, coarse failure reason).
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  return children;
}
